import { ApolloError, useApolloClient } from '@apollo/client'
import { EMBED_ID_HEADER, VIEW_CONTENT_ID_HEADER } from '@doss/common-all'
import type { TableMappings } from '@doss/configbase-common/apps'
import { isFrameQueryResultForRequest } from '@doss/guest-frame'
import { useEffect, useMemo, useState } from 'react'
import { Button } from '~/@/components/ui/button'
import {
  AppTableDistinctDocument,
  AppTableRowCountDocument,
  AppTableRowsDocument,
  CallEndpointDocument,
  type CallEndpointMutation,
  type CallEndpointMutationVariables,
  useGetViewContentForEmbedQuery,
  GetUdsSchemaForEmbedDocument,
  type GetUdsSchemaForEmbedQuery,
  RunUdsSqlBatchDocument,
  type RunUdsSqlBatchQuery,
  type RunUdsSqlBatchQueryVariables,
  RunUdsSqlDocument,
  type RunUdsSqlQuery,
  type RunUdsSqlQueryVariables,
  useUpdateEmbedViewContentMutation,
  useUpdatedViewContentSubscription,
  useUpsertViewContentMutation,
} from '~/common/generated/apollo-operations'
import { DossSignalType, useDossSignals } from '~/common/hooks/useDossSignals'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { createTraceparent, traceIdOfTraceparent } from '~/common/telemetry/timing-link'
import { DynamicAppBootup } from '~/components/guest-frame/DynamicAppBootup'
import { GuestFrame } from '~/components/guest-frame/GuestFrame'
import type { GuestFrameRoute, GuestNavigationIntent } from '~/components/guest-frame/GuestFrame'
import {
  useGuestDestinationHandler,
  useGuestDestinationResolver,
  useHostNavigationHandler,
} from '~/components/guest-frame/host-navigation'
import {
  EndpointCallError,
  GuestFrameRuntimeProvider,
  type GetUdsSchema,
  type RunEndpoint,
  type RunGraphql,
  type RunSql,
  type RunSqlBatch,
} from '~/components/guest-frame/transport'
import { useDossSnackbar } from '~/components/shared/DossSnackbar'
import { EmbedSourceEditor } from './EmbedSourceEditor'

// The host's copy of each App row document a frame may name.
const APP_TABLE_ROW_DOCUMENTS = {
  AppTableRows: AppTableRowsDocument,
  AppTableRowCount: AppTableRowCountDocument,
  AppTableDistinct: AppTableDistinctDocument,
} as const

const endpointCallErrorOf = (error: unknown): EndpointCallError => {
  const answered = error instanceof ApolloError ? error.graphQLErrors.at(0) : undefined
  if (answered === undefined) {
    return new EndpointCallError(error instanceof Error ? error.message : String(error))
  }
  const { code, status } = answered.extensions ?? {}
  const fallback = code === 'INTERNAL_SERVER_ERROR' ? 500 : 400
  return new EndpointCallError(
    answered.message,
    typeof code === 'string' ? code : undefined,
    typeof status === 'number' ? status : fallback,
  )
}

type UdsSqlGqlResult = RunUdsSqlQuery['runUdsSql']
type UdsSqlTransportOptions = Parameters<RunSql>[1]

// Map one current page to one GraphQL statement. Continuations create later transport requests;
// this helper neither loops nor accumulates pages.
const statementVariablesOf = (sql: string, options: UdsSqlTransportOptions) => ({
  sql,
  page: options?.page ?? null,
  includeTotalCount: options?.includeTotalCount === true,
})

const mapUdsSqlResult = (
  result: UdsSqlGqlResult,
  options: UdsSqlTransportOptions,
  traceId: string,
) => {
  if (options?.page === undefined) {
    // Every well-formed transport request carries a page.
    throw new Error('Pageless SQL request unexpectedly produced a result')
  }
  const pageResult = {
    rows: result.rows as Record<string, unknown>[],
    hasNext: result.hasNext,
    ...(result.offset == null ? {} : { offset: result.offset }),
    ...(result.cursor == null ? {} : { cursor: result.cursor }),
    ...(result.totalCount == null ? {} : { totalCount: result.totalCount }),
  }
  if (
    !isFrameQueryResultForRequest({ type: 'guest-query-result', id: 0, ...pageResult }, options)
  ) {
    throw new Error('Paginated SQL response did not match its request')
  }
  return { ...pageResult, resultBytes: result.resultBytes, traceId }
}

export type RenderEmbedProps = {
  embedId: string
  embedName: string
  viewContentId: string | null
  // Embed.betaWatermarkDisabled — flipped directly in the DB. The BETA tiling is hidden by
  // default (null/true); an explicit false re-enables it for that embed.
  betaWatermarkDisabled?: boolean | null
  // URL sync for the guest router; grid mounts pass nothing (memory mode).
  route?: GuestFrameRoute
  onGuestNavigate?: (intent: GuestNavigationIntent) => void
  onGuestHistoryGo?: (delta: number) => void
  onGuestTitle?: (title: string) => void
}

/**
 * Renders an EMBED view: sandboxed guest content when attached, a minimal paste-TSX editor when
 * empty. Visibility is gated server-side by the EmbedViewType flag (the API omits embed views
 * when it's off), so this component only ever mounts when embeds are enabled.
 */
export const RenderEmbed = ({
  embedId,
  embedName,
  viewContentId,
  betaWatermarkDisabled,
  route,
  onGuestNavigate,
  onGuestHistoryGo,
  onGuestTitle,
}: RenderEmbedProps) => {
  const { t } = useTypedTranslation()
  const { showError } = useDossSnackbar()
  const { subscribe, unsubscribe } = useDossSignals()
  // Same-origin links requested from the guest: SPA-navigate, or a new tab on modified clicks.
  // Every embed mount renders under the router, so the hook is safe here and the mounts need no wiring of their own.
  const onHostNavigationRequest = useHostNavigationHandler()
  const onGuestDestinationRequest = useGuestDestinationHandler()
  const resolveGuestDestination = useGuestDestinationResolver()
  const client = useApolloClient()
  // Local link state: a first save renders immediately without refetching the workspace tree.
  const [contentId, setContentId] = useState(viewContentId)
  // Referentially stable: a new identity would remount the guest frame (see GuestFrame deps).
  const telemetryContext = useMemo(
    () => ({ embedId, ...(contentId === null ? {} : { viewContentId: contentId }) }),
    [embedId, contentId],
  )
  const runSql = useMemo<RunSql>(
    () => async (sql, options) => {
      // One trace per fetch: the server /graphql span parents under this id, and the
      // guest_query telemetry event carries it, joining browser timing to the backend trace.
      const traceparent = options?.traceparent ?? createTraceparent()
      const { data } = await client.query<RunUdsSqlQuery, RunUdsSqlQueryVariables>({
        query: RunUdsSqlDocument,
        variables: statementVariablesOf(sql, options),
        fetchPolicy: 'no-cache',
        // Dashboard attribution for the backend uds.query span and audit log
        // (parseEmbedClientContext on the API side). Observability only.
        context: {
          traceparent,
          headers: {
            [EMBED_ID_HEADER]: embedId,
            ...(contentId === null ? {} : { [VIEW_CONTENT_ID_HEADER]: contentId }),
          },
        },
      })
      return mapUdsSqlResult(data.runUdsSql, options, traceIdOfTraceparent(traceparent))
    },
    [client, embedId, contentId],
  )
  // App row reads run the host's own copy of the named document, as the viewer.
  const runGraphql = useMemo<RunGraphql>(
    () => async (operation, variables) => {
      const { data } = await client.query<unknown>({
        query: APP_TABLE_ROW_DOCUMENTS[operation],
        variables,
        fetchPolicy: 'no-cache',
        context: {
          traceparent: createTraceparent(),
          headers: {
            [EMBED_ID_HEADER]: embedId,
            ...(contentId === null ? {} : { [VIEW_CONTENT_ID_HEADER]: contentId }),
          },
        },
      })
      return data
    },
    [client, embedId, contentId],
  )
  const runEndpoint = useMemo<RunEndpoint>(
    () =>
      async ({ logicalId, input }) => {
        try {
          const { data } = await client.mutate<CallEndpointMutation, CallEndpointMutationVariables>(
            {
              mutation: CallEndpointDocument,
              variables: { input: { logicalId, input } },
              fetchPolicy: 'no-cache',
              // The same attribution as a query. Observability only.
              context: {
                traceparent: createTraceparent(),
                headers: {
                  [EMBED_ID_HEADER]: embedId,
                  ...(contentId === null ? {} : { [VIEW_CONTENT_ID_HEADER]: contentId }),
                },
              },
            },
          )
          return data?.callEndpoint.output ?? null
        } catch (error: unknown) {
          throw endpointCallErrorOf(error)
        }
      },
    [client, embedId, contentId],
  )
  const runSqlBatch = useMemo<RunSqlBatch>(
    () => async (statements) => {
      const firstStatement = statements.at(0)
      if (firstStatement === undefined) {
        return []
      }
      // The first-render commit is the explicit authorization boundary. Every
      // statement shares this fetch trace and one backend snapshot session.
      const traceparent = firstStatement.options?.traceparent ?? createTraceparent()
      const { data } = await client.query<RunUdsSqlBatchQuery, RunUdsSqlBatchQueryVariables>({
        query: RunUdsSqlBatchDocument,
        variables: {
          statements: statements.map(({ sql, options }) => statementVariablesOf(sql, options)),
        },
        fetchPolicy: 'no-cache',
        context: {
          traceparent,
          headers: {
            [EMBED_ID_HEADER]: embedId,
            ...(contentId === null ? {} : { [VIEW_CONTENT_ID_HEADER]: contentId }),
          },
        },
      })
      const traceId = traceIdOfTraceparent(traceparent)
      return data.runUdsSqlBatch.statements.map((statement, index) => {
        if (statement.result !== null) {
          try {
            return {
              result: mapUdsSqlResult(statement.result, statements.at(index)?.options, traceId),
            }
          } catch (error) {
            return { error: { message: error instanceof Error ? error.message : String(error) } }
          }
        }
        if (statement.error === null) {
          return { error: { message: t('embed.queryError') } }
        }
        return {
          error: {
            message: statement.error.message,
            ...(statement.error.code === null ? {} : { code: statement.error.code }),
            ...(statement.error.limitBytes === null
              ? {}
              : { limitBytes: statement.error.limitBytes }),
            ...(statement.error.resultBytes === null
              ? {}
              : { resultBytes: statement.error.resultBytes }),
            ...(statement.error.parameter === null
              ? {}
              : { parameter: statement.error.parameter as 'maxRows' | 'page.limit' }),
            ...(statement.error.requestedRows === null
              ? {}
              : { requestedRows: statement.error.requestedRows }),
            ...(statement.error.maxRows === null ? {} : { maxRows: statement.error.maxRows }),
          },
        }
      })
    },
    [client, embedId, contentId, t],
  )

  // The UDS-queryable schema, assembled from the RLS-enforced tables query:
  // infrastructure columns first, then application columns in UDS catalog
  // order (order, then id) — the same shape the UDS translator authorizes.
  const getUdsSchema = useMemo<GetUdsSchema>(
    () => async () => {
      const { data } = await client.query<GetUdsSchemaForEmbedQuery>({
        query: GetUdsSchemaForEmbedDocument,
        fetchPolicy: 'no-cache',
      })
      return data.tables.map((table) => ({
        slug: table.slug,
        columns: [
          ...table.udsSystemColumns.map((column) => ({
            slug: column.slug,
            dataType: column.dataType,
            isNullable: column.isNullable,
          })),
          ...[...table.columns]
            .sort((a, b) => a.order - b.order || a.id.localeCompare(b.id))
            .map((column) => ({
              slug: column.slug,
              dataType: column.udsDataType,
              isNullable: column.isNullable,
            })),
        ],
      }))
    },
    [client],
  )

  const [editing, setEditing] = useState(contentId === null)
  const [draft, setDraft] = useState('')
  // True while the editor holds a legacy (in-app) draft, so a CLI publication arriving mid-edit
  // can block Save without discarding the text.
  const [legacyDraft, setLegacyDraft] = useState(contentId === null)

  const contentQuery = useGetViewContentForEmbedQuery({
    variables: { id: contentId ?? '' },
    skip: contentId === null,
  })
  // Live-update: a new ViewContent version writes the same normalized entity into the Apollo cache,
  // so the content query recomputes and GuestFrame re-renders — no refetch or manual refresh.
  useUpdatedViewContentSubscription({
    variables: { id: contentId ?? '' },
    skip: contentId === null,
  })
  const [upsertViewContent, { loading: saving }] = useUpsertViewContentMutation()
  const [updateEmbedViewContent] = useUpdateEmbedViewContentMutation()

  const viewContent = contentQuery.data?.viewContent
  const content = viewContent?.content ?? null
  // A version pinned to an SDK release always renders through that pinned ESM runtime. Versions
  // without a pin (every version until the App is republished from the CLI) keep the legacy path.
  // Keyed on content, not object identity: GuestFrame keys its boot effect on this object, and a
  // fresh graph object per query result (or a fresh literal per render) would restart the
  // handshake under a frame that has already initialized.
  const contentHash = viewContent?.contentHash ?? null
  const graphJson =
    viewContent?.appSdkGraph == null ? null : JSON.stringify(viewContent.appSdkGraph)
  // The version's deploy-resolved table mappings, keyed on content like the graph. They are fixed
  // per version, like the bundle: a schema change reaches the App once a redeploy publishes a new
  // version, which remounts the frame (GuestFrame is keyed on the version below).
  const mappingsJson =
    viewContent?.tableMappings == null ? null : JSON.stringify(viewContent.tableMappings)
  const esm = useMemo(
    () =>
      graphJson === null || contentHash === null
        ? null
        : {
            sha256: contentHash,
            graph: JSON.parse(graphJson) as unknown,
            ...(mappingsJson === null
              ? {}
              : { mappings: JSON.parse(mappingsJson) as TableMappings }),
          },
    [contentHash, graphJson, mappingsJson],
  )

  const snapshotManaged = viewContent?.sourceSnapshotHash != null
  // Managed or pinned source is published from the CLI, so the in-app editor is off for it.
  const readOnly = snapshotManaged || esm !== null
  const staleDraft = readOnly && legacyDraft

  // The workspace view context menu owns the "Edit source" entry point; it signals the
  // embed it targets, and the matching renderer switches into the editor.
  useEffect(() => {
    const id = subscribe(DossSignalType.EDIT_EMBED_SOURCE, async (input: { embedId: string }) => {
      if (input.embedId === embedId) {
        if (!readOnly) {
          setDraft(content ?? '')
        }
        setLegacyDraft(!readOnly)
        setEditing(true)
      }
    })
    return () => {
      unsubscribe(id)
    }
  }, [content, embedId, readOnly, subscribe, unsubscribe])

  const handleSave = async (): Promise<void> => {
    try {
      if (readOnly) {
        throw new Error(t('embed.draftBecameManaged'))
      }
      const upserted = await upsertViewContent({
        variables: { input: { id: contentId ?? undefined, content: draft } },
      })
      const savedId = upserted.data?.upsertViewContent.id
      if (savedId === undefined) {
        throw new Error(t('embed.saveError'))
      }
      // First save links the fresh content to this embed. Later saves append a new immutable
      // version under the same id — Apollo writes it to the normalized ViewContent entity, so the
      // watched content query re-renders on its own (no refetch needed).
      if (contentId === null) {
        await updateEmbedViewContent({ variables: { embedId, viewContentId: savedId } })
        setContentId(savedId)
      }
      setEditing(false)
    } catch (error: unknown) {
      showError(error instanceof Error ? error.message : t('embed.saveError'))
    }
  }

  if (editing) {
    // A CLI-managed or built version has no in-app editor: a short notice replaces it. A legacy
    // draft that was open when such a publication arrived stays visible for copying, minus Save.
    return (
      <div className="flex size-full flex-col gap-2 p-2">
        {staleDraft ? <p role="alert">{t('embed.draftBecameManaged')}</p> : null}
        {readOnly && !staleDraft ? (
          <p className="px-1 text-xs text-gray-11" role="status">
            {t('embed.managedSourceNotice')}
          </p>
        ) : (
          <div className="min-h-0 grow overflow-hidden rounded-md border border-gray-4 bg-white">
            <EmbedSourceEditor
              ariaLabel={t('embed.editSource')}
              onChange={setDraft}
              placeholder={t('embed.editorPlaceholder')}
              value={draft}
            />
          </div>
        )}
        <div className="flex shrink-0 items-center justify-end gap-2">
          {contentId !== null ? (
            <Button onClick={() => setEditing(false)} size="xs" variant="tertiary">
              {readOnly ? t('embed.close') : t('embed.cancel')}
            </Button>
          ) : null}
          {readOnly ? null : (
            <Button
              disabled={saving || draft.trim().length === 0}
              onClick={() => {
                void handleSave()
              }}
              size="xs"
            >
              {t('embed.save')}
            </Button>
          )}
        </div>
      </div>
    )
  }

  if (contentQuery.loading) {
    return <DynamicAppBootup />
  }

  if (contentQuery.error !== undefined || content === null) {
    return (
      <div className="flex size-full items-center justify-center p-4 text-xs text-red-9">
        {t('embed.loadError')}
      </div>
    )
  }

  return (
    <div className="size-full p-1">
      <GuestFrameRuntimeProvider
        getUdsSchema={getUdsSchema}
        runEndpoint={runEndpoint}
        runGraphql={runGraphql}
        runSql={runSql}
        runSqlBatch={runSqlBatch}
      >
        <GuestFrame
          // A booted frame never re-reads its init, so each version boots a fresh one.
          key={viewContent?.latestVersion}
          esm={esm}
          guestSpaceHrefPrefix={`/embed/${embedId}`}
          onGuestDestinationRequest={onGuestDestinationRequest}
          onGuestHistoryGo={onGuestHistoryGo}
          onGuestNavigate={onGuestNavigate}
          onGuestTitle={onGuestTitle}
          onHostNavigationRequest={onHostNavigationRequest}
          resolveGuestDestination={resolveGuestDestination}
          route={route}
          showBetaWatermark={betaWatermarkDisabled === false}
          source={content}
          telemetryContext={telemetryContext}
          title={embedName}
        />
      </GuestFrameRuntimeProvider>
    </div>
  )
}
